
import { useState, useEffect } from 'react';
import SafeMotion from '@/components/motion/SafeMotion';
import MotionErrorBoundary from '@/components/motion/MotionErrorBoundary';
import { Diamond, Gem, Watch, Circle } from "lucide-react";
import { type CarouselApi } from "@/components/ui/carousel";
import { useHeroData, MappedHeroContent, MappedHeroFeaturedProduct } from '@/hooks/useHeroData';
import HeroLoadingSkeleton from './HeroLoadingSkeleton';
import HeroContent from './HeroContent';
import HeroCarousel from './HeroCarousel';

const fallbackHeroContent: MappedHeroContent[] = [
  {
    supertitle: "Timeless Elegance, Modern Design",
    titleLine1: "Where Luxury",
    titleLine2: "Meets",
    titleHighlight: "Legacy",
    subtitle: "Discover curated collections of unparalleled craftsmanship, designed to be cherished for generations.",
    cta1: { text: "Explore Collections", path: "/products" },
    cta2: { text: "Our Philosophy", path: "/categories" },
  },
  {
    supertitle: "Exquisite Craftsmanship",
    titleLine1: "The Artisan's",
    titleLine2: "Signature",
    titleHighlight: "Touch",
    subtitle: "Each piece is a masterpiece, a testament to the dedication and skill of master craftsmen.",
    cta1: { text: "View New Arrivals", path: "/products?sort=newest" },
    cta2: { text: "Meet The Artisans", path: "/about" },
  },
  {
    supertitle: "For The Discerning Collector",
    titleLine1: "A Cut Above",
    titleLine2: "The",
    titleHighlight: "Rest",
    subtitle: "Experience the pinnacle of luxury with our exclusive, limited-edition jewelry and timepieces.",
    cta1: { text: "Shop Limited Editions", path: "/products?tag=limited" },
    cta2: { text: "Book a Consultation", path: "/contact" },
  },
];

const fallbackFeaturedProducts: MappedHeroFeaturedProduct[] = [
  { icon: Diamond, name: "Ethereal Diamond", category: "Necklaces" },
  { icon: Watch, name: "Chronos Legacy", category: "Timepieces" },
  { icon: Gem, name: "Imperial Sapphire", category: "Rings" },
  { icon: Circle, name: "Eternity Band", category: "Wedding" },
];

const EnhancedMobileOptimizedHero = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [api, setApi] = useState<CarouselApi>();

  const { data, isLoading, isError } = useHeroData();

  const heroContent = (!isLoading && !isError && data?.heroContent && data.heroContent.length > 0)
    ? data.heroContent
    : fallbackHeroContent;
  
  const featuredProducts = (!isLoading && !isError && data?.featuredProducts && data.featuredProducts.length > 0)
    ? data.featuredProducts
    : fallbackFeaturedProducts;

  const settings = (!isLoading && !isError && data?.settings)
    ? data.settings
    : { content_interval_ms: 8000, carousel_interval_ms: 5000 };

  useEffect(() => {
    if (heroContent.length <= 1) return;
    const contentInterval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % heroContent.length);
    }, settings.content_interval_ms); // Change content every X seconds

    return () => clearInterval(contentInterval);
  }, [heroContent.length, settings.content_interval_ms]);

  useEffect(() => {
    if (!api) {
      return;
    }
    const carouselInterval = setInterval(() => {
      if (api.canScrollNext()) {
        api.scrollNext();
      } else {
        api.scrollTo(0);
      }
    }, settings.carousel_interval_ms); // Change slide every Y seconds
    return () => clearInterval(carouselInterval);
  }, [api, settings.carousel_interval_ms]);

  const currentContent = heroContent[currentIndex];

  if (isLoading) {
    return <HeroLoadingSkeleton />;
  }
  
  if (!currentContent) {
    return null;
  }

  return (
    <MotionErrorBoundary>
      <section 
        className="relative overflow-hidden pt-20 pb-16 sm:pb-20 lg:pb-24 bg-background"
        aria-labelledby="hero-heading"
      >
        {/* Abstract background shapes */}
        <SafeMotion 
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-luxury-gold/5 rounded-full blur-3xl"
          animate={{ 
            x: [0, 50, -30, 0],
            y: [0, -40, 20, 0],
            scale: [1, 1.2, 0.9, 1]
          }}
          transition={{ 
            duration: 25, 
            repeat: Infinity, 
            ease: "easeInOut" 
          }}
          fallback={<div className="absolute top-1/4 left-1/4 w-96 h-96 bg-luxury-gold/5 rounded-full blur-3xl"></div>}
        >
          <div />
        </SafeMotion>
        <SafeMotion 
          className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl"
          animate={{ 
            x: [0, -60, 40, 0],
            y: [0, 30, -20, 0],
            scale: [1, 0.8, 1.1, 1]
          }}
          transition={{ 
            duration: 28, 
            repeat: Infinity, 
            ease: "easeInOut",
            delay: 3
          }}
          fallback={<div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl"></div>}
        >
          <div />
        </SafeMotion>
      
      <div className="w-full px-4 md:px-8 lg:px-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-start py-8 sm:py-12 lg:py-16">
          <HeroContent content={currentContent} currentIndex={currentIndex} />
          <HeroCarousel featuredProducts={featuredProducts} setApi={setApi} />
        </div>
      </div>
    </section>
    </MotionErrorBoundary>
  );
};

export default EnhancedMobileOptimizedHero;
